/**
 * A lazy page file that can no longer be fetched.
 *
 * In production that means a deploy replaced the files while this tab was
 * open; in development, that the dev server restarted or rebuilt the file. In
 * both cases a reload fetches the current files and the page simply works,
 * so it is treated as "out of date", never shown to anyone as an error.
 */
export function isStaleChunk(error) {
  const message = String(error?.message ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  );
}

const RELOAD_KEY = 'stale-chunk-reloaded-at';
/** A second failure inside this window means reloading does not help. */
const RETRY_WINDOW_MS = 10_000;

/**
 * Reload once to pick up the new files. Returns false - and does nothing -
 * when a reload was already tried moments ago, so a file that is genuinely
 * missing cannot trap the tab in a reload loop; the caller then shows its
 * recovery screen instead.
 */
export function reloadOnceForStaleChunk() {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - last < RETRY_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (private mode, blocked): without the loop guard,
    // do not reload automatically; the recovery screen still offers it.
    return false;
  }
  window.location.reload();
  return true;
}
